import Vue from 'vue';
import {API_ENTRYPOINT} from './entrypoint';

// Runtime feature flagy pro postupný redesign (mobilní verze) - umožňují
// stavět nové UI "bokem" a za běhu ho přepínat proti stávajícímu. Zdroj
// pravdy: reaktivní stav (Vue.observable) persistovaný do localStorage.
// Override přes URL: ?jméno_flagu=1 (nebo =0) - u LOCKED_ON flagů bez efektu
// (viz níže). Přepínač je v debug panelu v App.vue (jen pro admina).

const STORAGE_KEY = 'pr.flags';

const DEFAULTS = {
  // BattleScreen.vue nahrazuje starou trojici Fight2.vue/Dungeon.vue/
  // ObstacleFight.vue - ty uz po zamknuti tohohle flagu (LOCKED_ON nize)
  // byly smazané, takze "=0" override uz nema co zobrazit.
  newBattleUi: true,
  // Jeden flag pro cely novy svet (shell M1 + forkovane journey/next
  // obrazovky). Stary layout je zmrazeny a zustava jako behavioralni
  // reference. Porad jde o obycejny (persistovany) flag/prepinac (viz
  // newUiFlag v App.vue) - da se kdykoliv rucne vratit na stary layout.
  newUi: true,
  // Theming faze 1 (viz architecture/frontend.md sekce "Theming") - jen
  // $vuetify.theme.dark toggle, zatim beze zmeny jednotlivych komponent.
  darkTheme: false,
  // Skin/material system (viz docs/superpowers/specs/2026-08-04-theme-skin-system-design.md)
  // - druha, na dark/light nezavisla osa. Zatim jen "normal", pripravene
  // pro dalsi skiny beze zmeny mechanismu (viz public/skins/*.css a App.vue
  // vymena <link> tagu). Admin-only pro tuhle fazi.
  skin: 'normal',
  // Alternativa k 100svh/100dvh shellu - na Androidu (Chrome/MIUI) je svh/dvh
  // nespolehlive (issues.chromium.org/issues/40891557), misto CSS jednotky se
  // skutecna viditelna vyska meri primo v JS (window.visualViewport, viz main.js).
  newVhFix: true,
  // Predstazeni obrazku/hudby na pozadi v BEZNE karte prohlizece (stranka
  // Options) - u nainstalovane PWA se toto vzdy deje automaticky bez ohledu
  // na tyhle flagy (viz main.js, isRunningAsInstalledApp()). V bezne karte
  // je to ~429 MB (obrazky) / ~81 MB (hudba), proto vychozi OFF a rozdelene
  // na dva samostatne prepinace.
  prefetchImages: false,
  prefetchMusic: false,
  // Automaticke potvrzovani "Are you sure?" dialogu (ConfirmDialog.vue,
  // stranka Options) - vychozi OFF. Vyjimky (dismiss pokemona, vstup do
  // Ligy) resi jednotliva volani open() sama, viz komentar v ConfirmDialog.vue.
  autoConfirm: false,
  // Zrcadleni par ovladacich prvku - appka je dnes navrzena pro leváka (Buy
  // sloupec/zavírací křížek popupů daleko od palce; hamburger vlevo, New day
  // vpravo). Vychozi FALSE = dnesni chovani, zapnuti je pro praváka - zrcadli
  // ResponsiveDialog.vue kriz, SkillListTable.vue Buy sloupec, TopBar.vue
  // mobil a NavDrawer.vue smer vysuvu.
  rightHanded: false,
  // Viditelnost toast pop-upu pro jednotlivé kategorie info zprav - vychozi
  // TRUE pro vsechny. Kategorie odpovidaji App\Enum\InfoMessageTypeEnum na backendu.
  notifyErr: true,
  notifyOther: true,
  notifyExp: true,
  notifyBadge: true,
  notifyPokemon: true,
  // Poradi tab-ikonek v TeamBottomSheet - CSV ID tabu, napr. "team,trainer,...".
  // Prazdny retezec = vychozi poradi (viz tabs: v TeamBottomSheet.vue data()).
  tabOrder: '',
  // Jazyk UI - 'hyb' (soucasny neformalni mix CZ/EN beze zmeny), 'cs', 'en'.
  // Viz docs/superpowers/specs/2026-08-24-i18n-cz-en-hyb-design.md.
  language: 'hyb',
};

function loadFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

// Natvrdo zapnute flagy - stara localStorage hodnota z drivejsiho testovani
// by jinak prepisovala novy vychozi stav napořád. Admin prepinac (newUiFlag
// v App.vue) jde pouzit jen pro DOCASNE srovnani behem jedne session (viz
// setFlag nize) - kazdy dalsi refresh se vrati zpatky na "zapnuto".
const LOCKED_ON = ['newUi', 'newVhFix', 'newBattleUi'];

const state = Vue.observable({
  ...DEFAULTS,
  ...loadFromStorage(),
});

// URL override (dočasný, nepersistuje se)
const params = new URLSearchParams(window.location.search);
Object.keys(DEFAULTS).forEach((name) => {
  if (params.has(name)) {
    const v = params.get(name);
    // Boolean coercion jen pro skutecne boolean flagy (puvodni chovani) -
    // string flagy (napr. "skin") se preberou tak, jak prisly v URL.
    state[name] = typeof DEFAULTS[name] === 'boolean' ? (v === '1' || v === 'true') : v;
  }
});

LOCKED_ON.forEach((name) => { state[name] = true; });

export const flags = state;

// Skin/material system - jediny zdroj pravdy pro seznam existujicich skinu
// (applySkin() v App.vue jinak neoveruje jmeno skinu proti nicemu, neplatna
// hodnota tise rozbije cely vzhled bez cesty zpet pres UI). Sdileno mezi
// App.vue/NavDrawer.vue selectem (:items) a fallbackem v applySkin().
//
// POZOR: hodnoty tohoto pole jsou realne nazvy souboru (public/skins/<jmeno>.css)
// i hodnoty ukladane do localStorage/URL - NEMENIT kvuli hezcimu popisku v
// selectu. Hezci popisek resi SKIN_LABELS + skinSelectItems() nize, values
// zustavaji tyhle holé slugy.
export const AVAILABLE_SKINS = ['normal', 'hitech'];

// Popisky pro skin select (App.vue/NavDrawer.vue) - skin bez zaznamu tady se
// zobrazi jen svym slugem (viz "normal"). "hitech" je rozpracovany koncept,
// proto "(experimental)" primo v UI.
const SKIN_LABELS = {
  hitech: 'hitech (experimental)',
};

export function skinSelectItems() {
  return AVAILABLE_SKINS.map((s) => ({text: SKIN_LABELS[s] || s, value: s}));
}

// Jazyky UI - jmena jazyku se v selectu zobrazuji vzdy ve svem vlastnim
// jazyce (beze prekladu, bezny UX vzor pro language picker), na rozdil od
// label samotneho selectu (viz Options.vue, $t('dashboard.options.languageLabel')).
export const AVAILABLE_LANGUAGES = ['hyb', 'cs', 'en'];

const LANGUAGE_LABELS = {
  hyb: 'HI (Hybrid CZ/EN)',
  cs: 'Čeština',
  en: 'English',
};

export function languageSelectItems() {
  return AVAILABLE_LANGUAGES.map((l) => ({text: LANGUAGE_LABELS[l] || l, value: l}));
}

// Klíče synchronizované na backend (Options.vue) - `newUi`/`newVhFix`/
// `newBattleUi` jsou LOCKED_ON a mimo rozsah (viz
// docs/superpowers/specs/2026-08-12-account-settings-sync-design.md).
const SYNCED_KEYS = ['darkTheme', 'skin', 'autoConfirm', 'rightHanded', 'prefetchImages', 'prefetchMusic', 'notifyErr', 'notifyOther', 'notifyExp', 'notifyBadge', 'notifyPokemon', 'tabOrder', 'language'];

function accountSettingsUrl() {
  return API_ENTRYPOINT + (API_ENTRYPOINT.endsWith('/') ? '' : '/') + 'api/account-settings';
}

function authHeaders(extra) {
  const token = sessionStorage.getItem('token');
  if (!token) {
    return null;
  }
  return Object.assign({Authorization: `Bearer ${token}`}, extra || {});
}

function persist() {
  const toStore = {};
  Object.keys(DEFAULTS).forEach((k) => { toStore[k] = state[k]; });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
}

// Fire-and-forget - volající (setFlag) nečeká na výsledek, appka běží dál
// na lokální (localStorage) hodnotě bez ohledu na to, jak PATCH dopadne.
function patchBackend(name, value) {
  const headers = authHeaders({'Content-Type': 'application/merge-patch+json'});
  if (!headers) {
    return;
  }
  fetch(accountSettingsUrl(), {
    method: 'PATCH',
    headers,
    body: JSON.stringify({[name]: value}),
  }).catch(() => {});
}

export function setFlag(name, value) {
  state[name] = value;
  persist();
  if (SYNCED_KEYS.includes(name)) {
    patchBackend(name, value);
  }
}

// Volá se na začátku session (fresh login/impersonate i reload s tokenem už
// v sessionStorage) - stáhne server hodnoty a přebije jimi lokální stav i
// localStorage cache. Chyba se tiše zahodí, appka zůstává na aktuálních hodnotách.
export function syncFlagsFromBackend() {
  const headers = authHeaders();
  if (!headers) {
    return Promise.resolve();
  }
  return fetch(accountSettingsUrl(), {headers})
      .then((response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then((data) => {
        if (!data) {
          return;
        }
        SYNCED_KEYS.forEach((key) => {
          if (data[key] !== undefined) {
            state[key] = data[key];
          }
        });
        persist();
      })
      .catch(() => {});
}
